import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef
} from "react";
import ErrorMessage from "../../shared/components/Error";
import Loading from "../../shared/components/Loading";
import FloatingPanelService from "../../shared/FloatingPanel";
import { TranslationService } from "../../../services/TranslationService";
import {
  RequiredManager,
  ValidationMessage
} from "../../shared/RequieredManager";
import {
  CheckBoxEditor,
  TextEditor,
  TextEditorNumber
} from "../../shared/components/Editors";
import { IOStatusListResponse } from "../../../entities/company/IOStatus/IOStatusListResponse";
import IOStatusService from "../../../services/IOStatusService";
import {
  IOStatusDependency,
  IOStatusEntity,
  DefaultValueEnum
} from "../../../entities/company/IOStatus/IOStatusEntity";
import CompanyService, {
  DataType,
  Entities
} from "../../../services/CompanyService";
import ToastContext from "../../shared/bootstrap/Toast";
import ModalService from "../../shared/bootstrap/modal/Modal";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";
import Dropdown from "../../shared/components/Dropdown";
import { CollapsableWithTtitle } from "../../shared/form/CollapsableWithTtitle";
import { DefaultModalProps } from "../../shared/bootstrap/DefaultModal";
import SwitchButton from "../../shared/components/SwitchButton";
import TooltipComponent from "../../shared/TooltipComponent";
import { getError } from "../../../utils/RequestUtils";

const InvoiceStatus = () => {
  const { translate } = TranslationService;
  const [response, setResponse] = useState<IOStatusListResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [statusPaymentPromise, setStatusPaymentPromise] = useState(undefined);

  const requestData = async () => {
    setLoading(true);
    const result = await IOStatusService.getList();
    if (result instanceof Error) {
      setError(true);
      setLoading(false);
      return;
    }
    result.list.unshift({
      Exclude: false,
      IOStatusDependencies: [],
      Public: false,
      IOStatusID: -1,
      PublicValue: "",
      Value: CompanyService.getNormalIOStatusName()!,
      AlternateDateAdditionalDefinitionID: null,
      ExcludeCashflow: false,
      IsPaymentPromise: false,
      ExpirationDaysPaymentPromise: "0"
    });

    //exist state payment promise
    const ioPromise = result.list.find((x) => x.IsPaymentPromise);
    setStatusPaymentPromise(ioPromise);
    setResponse(result);
    setLoading(false);
    setError(false);
  };

  const requestDataCallback = useCallback(requestData, []);

  useEffect(() => {
    requestDataCallback();
  }, [requestDataCallback]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <ErrorMessage
        onRefresh={requestDataCallback}
        message={translate.ErrorLoadingList}
      />
    );
  }

  const showInvoiceStatusEdit = (invoiceStatus?: IOStatusEntity) => {
    FloatingPanelService.showPanel({
      children: (
        <InvoiceStatusEdit
          reload={requestDataCallback}
          invoiceStatus={invoiceStatus}
          ioStatusPromise={statusPaymentPromise}
        />
      ),
      title: invoiceStatus
        ? translate.EditInvoiceStatus
        : translate.NewInvoiceStatus,
      width: 1200,
      height: 600,
      position: "center"
    });
  };

  return (
    <div className="main-layout-container mx-3">
      <div className="main-panel">
        <h4 className="mb-2">{TranslationService.translate.EstadosFacturas}</h4>
        <div className="card">
          <div className="d-flex align-items-center justify-content-end mb-2">
            {CompanyService.canDo("editiostatus") && (
              <button
                className="btn btn-primary"
                onClick={() => showInvoiceStatusEdit(undefined)}
              >
                <i className="fal fa-plus" /> {translate.NewInvoiceStatus}
              </button>
            )}
          </div>
          <table className="table shadow-hover">
            <thead>
              <tr>
                <th>{translate.Value}</th>
                <th style={{ width: 200 }} className="text-center">
                  {translate.Exclude}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {response?.list.map((x) => (
                <tr
                  key={x.IOStatusID}
                  className="pointer show-child-on-hover"
                  onClick={(event) => {
                    showInvoiceStatusEdit(x);
                    event.stopPropagation();
                  }}
                >
                  <td>{x.Value}</td>
                  <td className="text-center">
                    {x.Exclude && <i className="fa fa-check"></i>}
                  </td>
                  <td width={400}></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Componente wrapper para Dropdown con tooltip condicional
const DropdownWithTooltip = ({
  text,
  minWidth = 100,
  children
}: {
  text: string;
  minWidth?: number;
  children: React.ReactElement;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !text) {
      setShowTooltip(false);
      return;
    }

    // Crear un elemento temporal para medir el ancho del texto
    const measureElement = document.createElement("span");
    measureElement.style.visibility = "hidden";
    measureElement.style.position = "absolute";
    measureElement.style.whiteSpace = "nowrap";
    measureElement.style.fontSize = window.getComputedStyle(
      containerRef.current
    ).fontSize;
    measureElement.style.fontFamily = window.getComputedStyle(
      containerRef.current
    ).fontFamily;
    measureElement.style.fontWeight = window.getComputedStyle(
      containerRef.current
    ).fontWeight;
    measureElement.textContent = text;
    document.body.appendChild(measureElement);

    const textWidth = measureElement.offsetWidth;
    document.body.removeChild(measureElement);
    // Mostrar tooltip solo si el texto es más ancho que el mínimo del dropdown
    setShowTooltip(textWidth > minWidth);
  }, [text, minWidth]);

  if (showTooltip && text) {
    return (
      <div ref={containerRef} style={{ minWidth: `${minWidth}px` }}>
        <TooltipComponent title={text}>{children}</TooltipComponent>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ minWidth: `${minWidth}px` }}>
      {children}
    </div>
  );
};

const InvoiceStatusEdit = ({
  reload,
  invoiceStatus,
  ioStatusPromise
}: {
  reload: () => void;
  invoiceStatus?: IOStatusEntity;
  ioStatusPromise?: IOStatusEntity;
}) => {
  const { translate } = TranslationService;
  const { showToast } = useContext(ToastContext);
  const [submiting, setSubmiting] = useState<string | null>(null);
  const [invoiceStatusModel, setInvoiceStatusModel] = useState<IOStatusEntity>({
    ...(invoiceStatus ?? {
      Exclude: false,
      IOStatusID: 0,
      Public: false,
      PublicValue: "",
      Value: "",
      IOStatusDependencies: [],
      AlternateDateAdditionalDefinitionID: null,
      ExcludeCashflow: false,
      IsPaymentPromise: false,
      ExpirationDaysPaymentPromise: "0"
    }),
    IOStatusDependencies: (invoiceStatus?.IOStatusDependencies ?? []).map(
      (dep) => {
        const hasDefaultValue =
          dep.DefaultValue !== null &&
          dep.DefaultValue !== undefined &&
          dep.DefaultValue !== 0;
        // Si DefaultValue está marcado, IsEditable debe ser false (prioridad sobre Mandatory)
        // Si no hay DefaultValue y Mandatory es true, IsEditable debe ser true
        // Si no hay DefaultValue y Mandatory es false, respetar el valor existente o true por defecto
        let isEditable: boolean;
        if (hasDefaultValue) {
          isEditable = false;
        } else if (dep.Mandatory) {
          isEditable = true;
        } else {
          isEditable =
            typeof dep.IsEditable === "boolean" ? dep.IsEditable : true;
        }

        return {
          ...dep,
          IsEditable: isEditable,
          DefaultValue: dep.DefaultValue !== undefined ? dep.DefaultValue : null
        };
      }
    )
  });

  const loadOAdditionals = (): { text: string; value: number }[] => {
    if (invoiceStatus?.IOStatusDependencies.length > 0) {
      const additionals = CompanyService.getAdditionalDefinitions().filter(
        (x) =>
          x.type === DataType.Date &&
          invoiceStatus?.IOStatusDependencies.some(
            (iostatus) =>
              iostatus.AdditionalDefinitionID === x.AdditionalDefinitionID
          )
      );
      return additionals.map((x) => ({
        text: x.Name,
        value: x.AdditionalDefinitionID
      }));
    }
    return [];
  };

  const [optionPromiseDates, setOptionPromiseDates] =
    useState<{ text: string; value: number }[]>(loadOAdditionals());
  const [value, setValue] = useState(invoiceStatus?.Value ?? "");
  const updateIoStatusDependency =
    <T extends keyof IOStatusDependency>(index: number, objKey: T) =>
    (value: IOStatusDependency[T]) =>
      setInvoiceStatusModel((x) => {
        const ios = x.IOStatusDependencies;
        const hasDefaultValue =
          ios[index].DefaultValue !== null &&
          ios[index].DefaultValue !== undefined &&
          ios[index].DefaultValue !== 0;

        if (objKey === "Mandatory" && typeof value === "boolean") {
          ios[index][objKey] = value;
          ios[index].Public = value;
          // Si Mandatory es true y no hay DefaultValue, IsEditable debe ser true automáticamente
          // Si hay DefaultValue, tiene prioridad y IsEditable debe ser false
          if (value && !hasDefaultValue) {
            ios[index].IsEditable = true;
          }
          // Si Mandatory es false y no hay DefaultValue, IsEditable puede ser true o false (el usuario puede elegir)
        } else if (objKey === "DefaultValue") {
          const newHasDefaultValue =
            value !== null && value !== undefined && value !== 0;
          ios[index][objKey] = value;

          // Si se marca DefaultValue, IsEditable debe ser false automáticamente (independientemente de otros estados)
          if (newHasDefaultValue) {
            ios[index].IsEditable = false;
          } else {
            // Si se desmarca DefaultValue, restaurar IsEditable según Mandatory
            // Si Mandatory está marcado, IsEditable debe ser true
            // Si Mandatory no está marcado, mantener el valor actual o permitir que el usuario lo cambie
            if (ios[index].Mandatory) {
              ios[index].IsEditable = true;
            }
          }
        } else if (objKey === "IsEditable") {
          // Si DefaultValue está marcado, no se puede cambiar IsEditable (debe ser false)
          if (hasDefaultValue) {
            return x;
          }
          // Si Mandatory está marcado y no hay DefaultValue, no se puede desmarcar IsEditable (debe ser true)
          if (ios[index].Mandatory && value === false) {
            return x;
          }
          // En otros casos, permitir el cambio
          ios[index][objKey] = value;
        } else {
          ios[index][objKey] = value;

          //add promesa de pago (solo cuando se actualiza AdditionalDefinitionID)
          if (objKey === "AdditionalDefinitionID") {
            const additional = CompanyService.getAdditionalDefinitions().find(
              (x) =>
                x.type === DataType.Date &&
                x.AdditionalDefinitionID.toString() === value?.toString()
            );
            if (additional) {
              setOptionPromiseDates([
                ...optionPromiseDates,
                {
                  text: additional.Name,
                  value: additional.AdditionalDefinitionID
                }
              ]);
            }

            // Limpiar DefaultValue si cambia el tipo de campo o si el DefaultValue no es válido para el nuevo tipo
            const newAdditionalDef =
              CompanyService.getAdditionalDefinitions().find(
                (x) => x.AdditionalDefinitionID.toString() === value?.toString()
              );
            const oldAdditionalDef =
              CompanyService.getAdditionalDefinitions().find(
                (x) =>
                  x.AdditionalDefinitionID.toString() ===
                  ios[index].AdditionalDefinitionID.toString()
              );

            const oldType = oldAdditionalDef?.type;
            const newType = newAdditionalDef?.type;
            const currentDefaultValue = ios[index].DefaultValue;

            // Si cambia el tipo de campo (texto <-> fecha) o el DefaultValue no es válido para el nuevo tipo, limpiar
            const shouldClearDefaultValue =
              oldType !== newType ||
              (newType === DataType.Text &&
                currentDefaultValue !== DefaultValueEnum.NombreYApellido &&
                currentDefaultValue !== null) ||
              (newType === DataType.Date &&
                currentDefaultValue !== DefaultValueEnum.FechaActual &&
                currentDefaultValue !== null) ||
              (newType !== DataType.Text &&
                newType !== DataType.Date &&
                currentDefaultValue !== null);

            if (shouldClearDefaultValue) {
              ios[index].DefaultValue = null;
              // Si había DefaultValue, restaurar IsEditable según Mandatory
              ios[index].IsEditable = ios[index].Mandatory ? true : true;
            }
          }
        }

        return {
          ...x,
          IOStatusDependencies: [...ios]
        };
      });

  const requiredManager = new RequiredManager();
  const onValueChanged = requiredManager.makeRequired(setValue);
  const { showDefaultModal } = ModalService;

  const addIOStatusDependency = () => {
    setInvoiceStatusModel((x) => {
      return {
        ...x,
        IOStatusDependencies: [
          ...x.IOStatusDependencies,
          {
            IOStatusDependencyID: 0,
            Mandatory: false,
            AdditionalDefinitionID: "",
            Public: false,
            IsEditable: true,
            DefaultValue: null
          }
        ]
      };
    });
  };

  const deleteIOStatusDependency = (index: number) => {
    const deleteElement = invoiceStatusModel.IOStatusDependencies.splice(
      index,
      1
    );
    setInvoiceStatusModel({ ...invoiceStatusModel });

    //borro opciones de promesa de pago
    if (deleteElement) {
      setOptionPromiseDates([
        ...optionPromiseDates.filter(
          (x) => x.value !== deleteElement[0].AdditionalDefinitionID
        )
      ]);
      if (
        invoiceStatusModel?.FieldPaymentPromise &&
        invoiceStatusModel?.FieldPaymentPromise ===
          deleteElement[0].AdditionalDefinitionID
      ) {
        setInvoiceStatusModel({
          ...invoiceStatusModel,
          FieldPaymentPromise: null
        });
      }
    }
  };

  const hasPaymentPromiseStatus = async () => {
    const modalProps: DefaultModalProps = {
      message: TranslationService.translate.ConfirmDeletePaymnetPromise,
      acceptButtonLabel: TranslationService.translate.Accept,
      onAcceptClick: async () => {
        onSubmit();
        ModalService.hideModal();
      }
    };
    showDefaultModal(modalProps);
  };

  const onSubmit = async () => {
    if (submiting !== null) {
      return;
    }
    if (!requiredManager.validate()) {
      showToast(translate.MissingRequiredFields);
      return;
    }
    setSubmiting("set");
    invoiceStatusModel.Value = value;
    invoiceStatusModel.IOStatusDependencies =
      invoiceStatusModel.IOStatusDependencies.filter(
        (x) => x.AdditionalDefinitionID
      ).map((dep) => {
        const hasDefaultValue =
          dep.DefaultValue !== null &&
          dep.DefaultValue !== undefined &&
          dep.DefaultValue !== 0;
        // Si DefaultValue está marcado, IsEditable debe ser false (prioridad sobre Mandatory)
        // Si no hay DefaultValue y Mandatory es true, IsEditable debe ser true
        // Si no hay DefaultValue y Mandatory es false, respetar el valor existente o true por defecto
        let isEditable: boolean;
        if (hasDefaultValue) {
          isEditable = false;
        } else if (dep.Mandatory) {
          isEditable = true;
        } else {
          isEditable =
            typeof dep.IsEditable === "boolean" ? dep.IsEditable : true;
        }

        return {
          ...dep,
          IsEditable: isEditable,
          DefaultValue: dep.DefaultValue !== undefined ? dep.DefaultValue : null
        };
      });
    const result = await IOStatusService.set(invoiceStatusModel);
    if (result instanceof Error) {
      const err = getError(result);
      showToast(
        err.status === 400 && err.message
          ? err.message
          : translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      setSubmiting(null);
      return;
    }

    // Actualizar el cache de additionalDefinitions con las nuevas IOStatusDependencies
    const companyAuth = CompanyService.getCompanyAuth();
    if (companyAuth) {
      const updatedAdditionalDefinitions =
        companyAuth.additionalDefinitions.map((additionalDef) => {
          // Buscar si este additionalDefinition tiene alguna dependencia con el IOStatusID que acabamos de guardar
          const existingDependencyIndex =
            additionalDef.IOStatusDependencies.findIndex((dep: any) => {
              // El IOStatusID puede estar en diferentes propiedades según la estructura
              const depIOStatusID =
                (dep as any).IOStatusID ?? (dep as any).iostatusid;
              return depIOStatusID === invoiceStatusModel.IOStatusID;
            });

          if (existingDependencyIndex >= 0) {
            // Actualizar la dependencia existente
            const updatedDependencies = [...additionalDef.IOStatusDependencies];
            const newDependency = invoiceStatusModel.IOStatusDependencies.find(
              (dep) =>
                dep.AdditionalDefinitionID ===
                additionalDef.AdditionalDefinitionID
            );

            if (newDependency) {
              const existingDep = updatedDependencies[
                existingDependencyIndex
              ] as any;
              updatedDependencies[existingDependencyIndex] = {
                ...existingDep,
                IsEditable: newDependency.IsEditable,
                DefaultValue: newDependency.DefaultValue,
                Mandatory: newDependency.Mandatory,
                Public: newDependency.Public
              } as any;
            } else {
              // Si no hay dependencia en el modelo, eliminar la dependencia del cache
              updatedDependencies.splice(existingDependencyIndex, 1);
            }

            return {
              ...additionalDef,
              IOStatusDependencies: updatedDependencies
            };
          } else {
            // Si no existe, agregar la nueva dependencia si está en el modelo
            const newDependency = invoiceStatusModel.IOStatusDependencies.find(
              (dep) =>
                dep.AdditionalDefinitionID ===
                additionalDef.AdditionalDefinitionID
            );

            if (newDependency) {
              return {
                ...additionalDef,
                IOStatusDependencies: [
                  ...additionalDef.IOStatusDependencies,
                  {
                    ...newDependency,
                    IOStatusDependencyID_ref: null
                  } as any
                ]
              };
            }
          }

          return additionalDef;
        });

      CompanyService.setCompanyAuth({
        ...companyAuth,
        additionalDefinitions: updatedAdditionalDefinitions
      } as any);
    }

    setSubmiting(null);
    reload();
    FloatingPanelService.hidePanel();
    showToast(translate.InvoiceStatusSaved, undefined, "success");
  };

  const onDelete = () => {
    const deleteTransactionType = async () => {
      if (submiting !== null) {
        return;
      }
      setSubmiting("del");
      const result = await IOStatusService.delete(
        invoiceStatusModel?.IOStatusID!
      );
      if (result instanceof Error) {
        showToast(translate.ErrorProcessingRequest, undefined, "danger");
        setSubmiting(null);
        return;
      }
      setSubmiting(null);
      reload();
      FloatingPanelService.hidePanel();
      showToast(translate.InvoiceStatusDeleted, undefined, "success");
    };
    ModalService.showDefaultModal({
      acceptButtonLabel: translate.Delete,
      message: () => (
        <>
          {translate.AreYouSureDeleteInvoiceStatus.replace(
            "{0}",
            CompanyService.getNormalIOStatusName()
          )
            .split("\n")
            .map((x) => (
              <>
                <span>{x}</span>
                <br />
              </>
            ))}
        </>
      ),
      onAcceptClick: deleteTransactionType,
      acceptButtonClassName: "btn btn-danger",
      title: TranslationService.translate.Delete,
      closeButtonLabel: TranslationService.translate.Cancel
    });
  };

  return (
    <>
      <div className="floatingBody p-4">
        <div className="card-body p-0">
          <div className="mb-4">
            <label className="form-label fw-normal mb-2">
              {translate.Name}
            </label>
            <TextEditor
              onChange={onValueChanged}
              defaultValue={value}
              maxLength={50}
            />
            {
              <ValidationMessage
                onChange={onValueChanged}
                defaultValue={value}
              />
            }
          </div>

          {invoiceStatusModel.IOStatusID > -1 && (
            <>
              {/* Configuracion */}
              {
                <CollapsableWithTtitle
                  title={translate.InvoceConfigurationTitle}
                  showDefault={true}
                >
                  <div className="mb-3">
                    <SwitchButton
                      label={translate.ShowClient}
                      onChange={(x) =>
                        setInvoiceStatusModel({
                          ...invoiceStatusModel,
                          Public: x
                        })
                      }
                      defaultValue={invoiceStatusModel?.Public}
                    />
                  </div>
                  {invoiceStatusModel.Public && (
                    <div className="mb-3">
                      <label className="form-label fw-normal mb-2">
                        {translate.InvocePublicName}
                      </label>
                      <TextEditor
                        onChange={(x) => {
                          setInvoiceStatusModel({
                            ...invoiceStatusModel,
                            PublicValue: x
                          });
                        }}
                        defaultValue={invoiceStatusModel?.PublicValue}
                      />
                    </div>
                  )}
                  <div className="mb-3">
                    <SwitchButton
                      label={translate.PaymentPromise}
                      onChange={(x) =>
                        setInvoiceStatusModel({
                          ...invoiceStatusModel,
                          IsPaymentPromise: x
                        })
                      }
                      defaultValue={invoiceStatusModel?.IsPaymentPromise}
                    />
                  </div>
                  <div className="mb-3">
                    <SwitchButton
                      label={translate.Exclude}
                      onChange={(x) =>
                        setInvoiceStatusModel({
                          ...invoiceStatusModel,
                          Exclude: x
                        })
                      }
                      defaultValue={invoiceStatusModel?.Exclude}
                    />
                  </div>
                  <div className="mb-3">
                    <SwitchButton
                      label={
                        TranslationService.translate.ExcludeFromCashflowTitle
                      }
                      onChange={(x) =>
                        setInvoiceStatusModel({
                          ...invoiceStatusModel,
                          ExcludeCashflow: x
                        })
                      }
                      defaultValue={invoiceStatusModel?.ExcludeCashflow}
                    />
                  </div>
                  {!invoiceStatusModel.ExcludeCashflow && (
                    <div className="mb-3">
                      <label className="form-label fw-normal mb-2">
                        {translate.AlternativeDateField}
                      </label>
                      <DropdownWithTooltip
                        text={(() => {
                          const selectedItem =
                            CompanyService.getAdditionalDefinitions()
                              .filter(
                                (x) =>
                                  x.type === DataType.Date &&
                                  x.Name !== TranslationService.translate.Date
                              )
                              .find(
                                (x) =>
                                  x.AdditionalDefinitionID ===
                                  invoiceStatusModel.AlternateDateAdditionalDefinitionID
                              );
                          return selectedItem?.Name || "";
                        })()}
                        minWidth={100}
                      >
                        <Dropdown
                          items={CompanyService.getAdditionalDefinitions()
                            .filter(
                              (x) =>
                                x.type === DataType.Date &&
                                x.Name !== TranslationService.translate.Date
                            )
                            .map((x) => ({
                              text: x.Name,
                              value: x.AdditionalDefinitionID
                            }))}
                          onChange={(x) => {
                            setInvoiceStatusModel({
                              ...invoiceStatusModel,
                              AlternateDateAdditionalDefinitionID: x ?? null
                            });
                          }}
                          defaultValue={
                            invoiceStatus?.AlternateDateAdditionalDefinitionID ??
                            null
                          }
                          optionLabel={TranslationService.translate.None}
                        />
                      </DropdownWithTooltip>
                    </div>
                  )}
                </CollapsableWithTtitle>
              }

              {/* Campos adicionales */}
              {
                <CollapsableWithTtitle
                  title={translate.InvoceAdditionalTitle}
                  showDefault={false}
                >
                  <div className="table-responsive">
                    <table className="table table-sm mt-2 mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: "30%" }}>
                            {translate.AdditionalField}
                          </th>
                          <th className="text-center" style={{ width: "12%" }}>
                            {translate.Mandatory}
                          </th>
                          <th className="text-center" style={{ width: "12%" }}>
                            {translate.Public}
                          </th>
                          <th className="text-center" style={{ width: "15%" }}>
                            Es Editable
                          </th>
                          <th className="text-center" style={{ width: "20%" }}>
                            Valor Default
                          </th>
                          <th
                            className="text-center"
                            style={{ width: "11%" }}
                          />
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceStatusModel?.IOStatusDependencies.map(
                          (x, index) => {
                            const additionalDef =
                              CompanyService.getAdditionalDefinitions().find(
                                (ad) =>
                                  ad.AdditionalDefinitionID.toString() ===
                                  x.AdditionalDefinitionID.toString()
                              );
                            const isTextType =
                              additionalDef?.type === DataType.Text;
                            const isDateType =
                              additionalDef?.type === DataType.Date;
                            const hasDefaultValue =
                              x.DefaultValue !== null &&
                              x.DefaultValue !== undefined &&
                              x.DefaultValue !== 0;
                            const defaultValueOptions = isTextType
                              ? [
                                  {
                                    text: "Nombre y Apellido",
                                    value: DefaultValueEnum.NombreYApellido
                                  }
                                ]
                              : isDateType
                                ? [
                                    {
                                      text: "Fecha Actual",
                                      value: DefaultValueEnum.FechaActual
                                    }
                                  ]
                                : [];

                            return (
                              <tr
                                key={
                                  index +
                                  "-" +
                                  x.AdditionalDefinitionID.toString()
                                }
                              >
                                <td className="dropdown-tooltip">
                                  <DropdownWithTooltip
                                    text={(() => {
                                      const selectedItem =
                                        CompanyService.getAdditionalDefinitions()
                                          .filter(
                                            (def) =>
                                              def.Entity === Entities.Invoice
                                          )
                                          .find(
                                            (def) =>
                                              def.AdditionalDefinitionID ===
                                              x.AdditionalDefinitionID
                                          );
                                      return selectedItem?.Name || "";
                                    })()}
                                  >
                                    <select
                                      className="form-select form-select-sm"
                                      onChange={(x) =>
                                        updateIoStatusDependency(
                                          index,
                                          "AdditionalDefinitionID"
                                        )(parseInt(x.target.value))
                                      }
                                      value={x.AdditionalDefinitionID as number}
                                    >
                                      <option
                                        value={undefined}
                                        style={{ display: "none" }}
                                      >
                                        {TranslationService.translate.Select}
                                      </option>
                                      {CompanyService.getAdditionalDefinitions()
                                        .filter(
                                          (x) => x.Entity === Entities.Invoice
                                        )
                                        .map((x) => (
                                          <option
                                            key={x.AdditionalDefinitionID}
                                            value={x.AdditionalDefinitionID}
                                          >
                                            {x.Name}
                                          </option>
                                        ))}
                                    </select>
                                  </DropdownWithTooltip>
                                </td>
                                <td className="text-center align-middle">
                                  <CheckBoxEditor
                                    onChange={updateIoStatusDependency(
                                      index,
                                      "Mandatory"
                                    )}
                                    value={x.Mandatory}
                                    message=""
                                    containerClass={{ display: "inline-block" }}
                                  ></CheckBoxEditor>
                                </td>
                                <td className="text-center align-middle">
                                  <CheckBoxEditor
                                    onChange={updateIoStatusDependency(
                                      index,
                                      "Public"
                                    )}
                                    value={x.Public}
                                    message=""
                                    containerClass={{ display: "inline-block" }}
                                  ></CheckBoxEditor>
                                </td>
                                <td className="text-center align-middle">
                                  <CheckBoxEditor
                                    onChange={updateIoStatusDependency(
                                      index,
                                      "IsEditable"
                                    )}
                                    value={x.IsEditable === true}
                                    message=""
                                    disabled={hasDefaultValue || x.Mandatory}
                                    containerClass={{ display: "inline-block" }}
                                  ></CheckBoxEditor>
                                </td>
                                <td className="text-center align-middle">
                                  {isTextType || isDateType ? (
                                    hasDefaultValue ? (
                                      <div className="d-flex align-items-center justify-content-center gap-2">
                                        <CheckBoxEditor
                                          containerClass={{
                                            minWidth: x.DefaultValue
                                              ? "auto"
                                              : "150px"
                                          }}
                                          onChange={(checked) => {
                                            if (checked) {
                                              const defaultValue = isTextType
                                                ? DefaultValueEnum.NombreYApellido
                                                : DefaultValueEnum.FechaActual;
                                              updateIoStatusDependency(
                                                index,
                                                "DefaultValue"
                                              )(defaultValue);
                                            } else {
                                              updateIoStatusDependency(
                                                index,
                                                "DefaultValue"
                                              )(null);
                                            }
                                          }}
                                          value={hasDefaultValue}
                                          message=""
                                        ></CheckBoxEditor>
                                        <div
                                          style={{
                                            minWidth: x.DefaultValue
                                              ? "auto"
                                              : "0"
                                          }}
                                        >
                                          <DropdownWithTooltip
                                            text={(() => {
                                              const selectedItem =
                                                defaultValueOptions.find(
                                                  (opt) =>
                                                    opt.value === x.DefaultValue
                                                );
                                              return selectedItem?.text || "";
                                            })()}
                                            minWidth={150}
                                          >
                                            <Dropdown
                                              items={defaultValueOptions}
                                              onChange={(value) =>
                                                updateIoStatusDependency(
                                                  index,
                                                  "DefaultValue"
                                                )(value ?? null)
                                              }
                                              defaultValue={
                                                x.DefaultValue ?? null
                                              }
                                              optionLabel={
                                                TranslationService.translate
                                                  .None
                                              }
                                            />
                                          </DropdownWithTooltip>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="d-flex justify-content-center">
                                        <CheckBoxEditor
                                          onChange={(checked) => {
                                            if (checked) {
                                              const defaultValue = isTextType
                                                ? DefaultValueEnum.NombreYApellido
                                                : DefaultValueEnum.FechaActual;
                                              updateIoStatusDependency(
                                                index,
                                                "DefaultValue"
                                              )(defaultValue);
                                            } else {
                                              updateIoStatusDependency(
                                                index,
                                                "DefaultValue"
                                              )(null);
                                            }
                                          }}
                                          value={hasDefaultValue}
                                          message=""
                                        ></CheckBoxEditor>
                                      </div>
                                    )
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td className="text-center align-middle">
                                  <div className="d-flex justify-content-center">
                                    <i
                                      className="far fa-fw pointer fa-trash text-danger"
                                      onClick={(event) => {
                                        deleteIOStatusDependency(index);
                                        event.stopPropagation();
                                      }}
                                    ></i>
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                        )}
                        <tr>
                          <td colSpan={6} className="border-0 pt-2">
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={addIOStatusDependency}
                            >
                              <i className="fal fa-plus me-1"></i>
                              {translate.Add}
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CollapsableWithTtitle>
              }

              {/* promesa de pago */}
              {invoiceStatusModel.IsPaymentPromise && (
                <CollapsableWithTtitle
                  title={translate.PaymentPromiseTitle}
                  showDefault={true}
                >
                  <div className="mb-3">
                    <label className="form-label fw-normal mb-2">
                      {translate.PromiseDate}
                    </label>
                    <DropdownWithTooltip
                      text={(() => {
                        const selectedItem = optionPromiseDates.find(
                          (opt) =>
                            opt.value ===
                            invoiceStatusModel?.FieldPaymentPromise
                        );
                        return selectedItem?.text || "";
                      })()}
                      minWidth={100}
                    >
                      <Dropdown
                        items={optionPromiseDates}
                        defaultValue={
                          invoiceStatusModel?.FieldPaymentPromise ?? null
                        }
                        optionLabel={TranslationService.translate.None}
                        onChange={requiredManager.makeRequiredWithId(
                          (value?: number) =>
                            setInvoiceStatusModel({
                              ...invoiceStatusModel,
                              FieldPaymentPromise: value ?? null
                            }),
                          "additional"
                        )}
                      />
                    </DropdownWithTooltip>
                    <ValidationMessage
                      onChange={requiredManager.getValidation("additional")}
                      defaultValue={invoiceStatusModel.FieldPaymentPromise}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-normal mb-2">
                      {translate.ExpirationDaysPaymentPromise}
                    </label>
                    <TextEditorNumber
                      onChange={(x: string) =>
                        setInvoiceStatusModel({
                          ...invoiceStatusModel,
                          ExpirationDaysPaymentPromise: x
                        })
                      }
                      defaultValue={
                        invoiceStatusModel?.ExpirationDaysPaymentPromise
                      }
                    />
                  </div>
                </CollapsableWithTtitle>
              )}
            </>
          )}
        </div>
      </div>

      <FloatingPanelFooter>
        {invoiceStatusModel.IOStatusID > 0 && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={onDelete}
          >
            {translate.Delete}
            {submiting === "del" && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        )}

        {CompanyService.canDo("editiostatus") && (
          <button
            className="btn btn-primary btn-sm"
            onClick={
              ioStatusPromise &&
              invoiceStatusModel.IsPaymentPromise &&
              ioStatusPromise?.IOStatusID !== invoiceStatus?.IOStatusID
                ? hasPaymentPromiseStatus
                : onSubmit
            }
          >
            {translate.Save}
            {submiting === "set" && (
              <i className="fas fa-spinner-third fa-spin third ms-2"></i>
            )}
          </button>
        )}
      </FloatingPanelFooter>
    </>
  );
};

export default InvoiceStatus;
